import { getSessionIdentity } from 'stores/sessionIdentity';
import { appText } from 'utils/appLocale';
import { TranslatableError } from 'utils/translatableError';
import { storage, type StorageKey } from 'utils/typedStorage';

/**
 * Returns the session org ID after auth is ready.
 * Returns null while the session is loading, after logout, or when invite
 * provisioning has not populated salv_org_id yet. Does not read localStorage.
 */
export function getValidOrgId(): string | null {
  return getSessionIdentity().orgId;
}

const ORG_NOT_LOADED_EN = 'Organization is not loaded';

/**
 * The org ID (by default the session's), for code paths that cannot run
 * without one. Throws when it is not loaded yet, so call it inside the
 * caller's existing error handling rather than asserting the ID is present.
 */
export function requireOrgId(orgId: string | null = getValidOrgId()): string {
  if (!orgId) {
    throw new TranslatableError((lng) =>
      lng === 'en'
        ? ORG_NOT_LOADED_EN
        : appText('organization.notLoaded', ORG_NOT_LOADED_EN)
    );
  }
  return orgId;
}

/**
 * Returns the session user ID after auth is ready.
 * Returns null while the session is loading or after logout.
 * Does not read localStorage.
 */
export function getValidUserId(): string | null {
  return getSessionIdentity().userId;
}

/**
 * Clears all localStorage keys except the specified one.
 * Commonly used during support user switching to preserve the auth token.
 */
export const clearLocalStorageExcept = (keepKey: StorageKey): boolean =>
  storage.clearExcept(keepKey);
